import React, {useState, useRef, useEffect } from 'react';
import dayjs from 'dayjs';

import { Box, IconButton, CircularProgress } from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';

import { VictoryChart, VictoryBoxPlot, VictoryTheme, VictoryAxis } from 'victory';
import * as d3 from 'd3-scale-chromatic';

const BoxPlotChart = ({ 
    data, isLoading, filter,
}) => {

    const chartRef = useRef(null);
    const filterRef = useRef(null);

    const [filterOpen, setFilterOpen] = useState(false);
    const [chartHeight, setChartHeight] = useState(0);

    const toggleFilter = () => {
        setFilterOpen(!filterOpen);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (filterRef.current && !filterRef.current.contains(event.target)) {
                const rect = filterRef.current.getBoundingClientRect();
                const topLeft = { x: rect.left, y: rect.top };
                const bottomRight = { x: rect.right, y: rect.bottom };
                if (event.clientX < topLeft.x || 
                    event.clientX > bottomRight.x) {
                    setFilterOpen(false);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const resizeObserver = new ResizeObserver(entries => {
          for (let entry of entries) {
            setChartHeight(entry.contentRect.height);
          }
        });
    
        if (chartRef.current) {
          resizeObserver.observe(chartRef.current);
        }
    
        return () => {
          if (chartRef.current) {
            resizeObserver.unobserve(chartRef.current);
          }
        };
      }, []);

    return (
        <Box>
        <Box 
        sx={{
            display: 'flex', 
            position: 'relative',
            width: '100%',
            transition: 'flex-grow 0.3s',
        }} 
        >
            <Box ref={chartRef} width='100%' sx={{backgroundColor: 'var(--background-color)', pr: 5}}>
                {
                isLoading ?
                <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: 500}}>
                    <CircularProgress />
                </Box>
                :
                <VictoryChart 
                width={1500} 
                height={700}
                padding={{ top: 50, bottom: 100, left: 75, right: 50 }}
                theme={VictoryTheme.material}
                scale={{ x: "time", y: "linear" }}
                >
                <VictoryAxis
                    tickFormat={(t) => dayjs(t).format('MM/DD/YYYY')}
                    style={{
                    axisLabel: { padding: 50 },
                    tickLabels: { textAnchor: 'end', fontSize: 20, },
                    }}
                />
                <VictoryAxis
                dependentAxis
                style={{
                  axisLabel: { padding: 40 },
                  tickLabels: { fontSize: 20 },
                }}
              />
                <VictoryBoxPlot
                    data={data}
                    style={{
                        min: { stroke: "tomato" },
                        max: { stroke: "tomato" },
                        q1: { fill: "tomato" },
                        q3: { fill: "tomato" },
                        median: { stroke: "white", strokeWidth: 2 },
                        minLabels: { fontSize: 20 },
                        maxLabels: { fontSize: 20 },
                        q1Labels: { fontSize: 20 },
                        q3Labels: { fontSize: 20 },
                        medianLabels: { fontSize: 20 },
                    }} />
                </VictoryChart>
                }
            </Box>
            {
            filter ?
            <Box
            ref={filterRef}
            sx={{
                width: filterOpen ? 350 : 30,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                border: 1,
                borderRadius: !filterOpen ? 1 : 3,
                mt: '10px',
                mr: 1,
                minHeight: chartHeight - 20,
                maxHeight: !filterOpen ? chartHeight - 20 : null,
                backgroundColor: !filterOpen ? 'var(--primary-color)' : 'var(--background-color)',
                borderColor: 'var(--primary-color)',
                px: 2,

                position: 'absolute',
                zIndex: 5,
                right: 0,
                top: 0,
                boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
            }}
            >

            <Box sx={{mb: 1}}>
            <IconButton onClick={() => toggleFilter()}>
                <FilterAltIcon sx={{ color: !filterOpen ? 'var(--background-color)' : 'var(--primary-color)' }} />
            </IconButton>
            </Box>

            <Box
            sx={{
                mb: 2,
                width: '100%',
            }}
            >
            {
                filterOpen ? filter : null
            }
            </Box>
            </Box>
            : null
            }
        </Box>
        </Box>
    );
}

export default BoxPlotChart;